import { createLogger } from "@/lib/logger";
import type { User } from "@supabase/supabase-js";

const logger = createLogger("auth-utils");
type AuthIdentity = Partial<Pick<User, "id" | "email">>;

/**
 * Optimistic auth check using localStorage before Supabase calls
 * This provides faster feedback for clearly unauthenticated users
 * Note: This returns true if ANY Supabase auth data exists, false only if clearly none exists
 */
export function hasStoredAuthSession(): boolean {
  try {
    // Supabase stores auth in keys like: sb-<project-ref>-auth-token
    // We'll check if ANY supabase auth keys exist
    const allKeys = Object.keys(localStorage);
    const supabaseAuthKeys = allKeys.filter(
      (key) => key.startsWith("sb-") && key.includes("-auth-token")
    );

    if (supabaseAuthKeys.length === 0) {
      logger.debug("No Supabase auth keys found in localStorage");
      return false;
    }

    // Check if any of the auth keys have valid data
    for (const key of supabaseAuthKeys) {
      try {
        const data = localStorage.getItem(key);
        if (!data) continue;

        const parsed = JSON.parse(data);

        // Check for access_token or refresh_token
        if (parsed?.access_token || parsed?.refresh_token) {
          logger.debug("Found Supabase auth session", { key });
          return true;
        }
      } catch (e) {
        // Skip invalid JSON
        continue;
      }
    }

    logger.debug("No valid Supabase sessions found");
    return false;
  } catch (error) {
    logger.warn("Error checking stored auth session", { error });
    // On error, return true to be defensive (let Supabase handle it)
    return true;
  }
}

/**
 * Fast auth check for paywall access
 * Returns true if user is likely authenticated, false if clearly not
 */
export function fastAuthCheck(): boolean {
  // Quick localStorage check first
  if (!hasStoredAuthSession()) {
    return false;
  }

  // Additional checks can be added here
  return true;
}

export type PaywallAuthGateDecision = "allow" | "wait" | "redirect";

interface ResolvePaywallAuthGateDecisionParams {
  authLoading: boolean;
  user: AuthIdentity | null;
  hasStoredSession: boolean;
  authGraceExpired: boolean;
}

/**
 * Decide whether the paywall should render, wait for auth hydration, or redirect.
 * This prevents redirect loops when the browser already has a Supabase session
 * but React auth context has not hydrated yet.
 */
export function resolvePaywallAuthGateDecision({
  authLoading,
  user,
  hasStoredSession,
  authGraceExpired,
}: ResolvePaywallAuthGateDecisionParams): PaywallAuthGateDecision {
  if (authLoading) {
    return "wait";
  }

  if (user?.id) {
    return "allow";
  }

  if (hasStoredSession && !authGraceExpired) {
    return "wait";
  }

  return "redirect";
}

/**
 * Prefer the active Supabase session user when available.
 * This avoids relying on React auth context hydration during session startup.
 */
export function resolveEffectiveAuthUser(
  contextUser: AuthIdentity | null,
  sessionUser: AuthIdentity | null
): AuthIdentity | null {
  if (sessionUser?.id) {
    return sessionUser;
  }

  if (contextUser?.id) {
    return contextUser;
  }

  return sessionUser ?? contextUser ?? null;
}

export function appendUserIdentityParams(params: URLSearchParams, user: AuthIdentity | null): void {
  if (!user) return;
  if (user.email) {
    params.append("userEmail", user.email);
  }
  if (user.id) {
    params.append("userId", user.id);
  }
}

export function applyUserIdentityMetadata(
  metadata: Record<string, unknown>,
  user: AuthIdentity | null
): void {
  if (!user) return;
  if (user.email) {
    metadata.userEmail = user.email;
  }
  if (user.id) {
    metadata.userId = user.id;
  }
}
